import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import StorageIcon from '@material-ui/icons/Storage';
import { InfoCard, EmptyState } from '@backstage/core-components';
import type { DevxpApi } from '../api';
import type { DeveloperPerformance, DeveloperMapping, RepoMapping } from '../types';
import { SpiderChart } from './SpiderChart';

const CATEGORY_COLORS = [
  '#3f51b5', '#e91e63', '#9c27b0', '#ff9800',
  '#4caf50', '#607d8b', '#00bcd4', '#f44336',
  '#795548', '#9e9e9e',
];

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  userId: {
    fontFamily: 'monospace',
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  skillChip: {
    margin: theme.spacing(0.25),
    fontSize: '0.7rem',
    height: 22,
  },
  categorySection: {
    marginBottom: theme.spacing(2),
  },
  categoryTitle: {
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  repoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(0.5),
    backgroundColor: theme.palette.action.hover,
  },
  repoName: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },
  chartSubtitle: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
}));

interface DeveloperDetailsProps {
  api: DevxpApi;
  userId: string;
  onBack: () => void;
  onCompare: (userId: string) => void;
  isInCompare: boolean;
}

export const DeveloperDetails = ({
  api,
  userId,
  onBack,
  onCompare,
  isInCompare,
}: DeveloperDetailsProps) => {
  const classes = useStyles();
  const [perf, setPerf] = useState<DeveloperPerformance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [repoNames, setRepoNames] = useState<Map<string, string>>(new Map());
  const [devNames, setDevNames] = useState<Map<string, string>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getDeveloperPerformance(userId);
      setPerf(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load developer performance');
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => {
    load();
    api.getRepoMappings()
      .then(({ mappings }: { mappings: RepoMapping[] }) => {
        const m = new Map<string, string>();
        mappings.forEach(rm => m.set(rm.masked_name, rm.real_name));
        setRepoNames(m);
      })
      .catch(() => {});
    api.getMappings()
      .then(({ mappings }: { mappings: DeveloperMapping[] }) => {
        const m = new Map<string, string>();
        mappings.forEach(dm => m.set(dm.masked_name, dm.real_name));
        setDevNames(m);
      })
      .catch(() => {});
  }, [load, api]);

  // Group unique skills by category
  const groupedSkills = React.useMemo(() => {
    if (!perf) return [];
    const map = new Map<string, string[]>();
    perf.uniqueSkills.forEach(s => {
      if (!map.has(s.category)) map.set(s.category, []);
      map.get(s.category)!.push(s.skillName);
    });
    return Array.from(map.entries()).map(([cat, skills]) => ({ cat, skills }));
  }, [perf]);

  return (
    <Box>
      {/* Back header */}
      <Box className={classes.header}>
        <Tooltip title="Back to leaderboard">
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="h6" className={classes.userId}>
          {devNames.get(userId) ?? userId}
        </Typography>
        <Tooltip title={isInCompare ? 'In comparison' : 'Add to comparison'}>
          <IconButton
            size="small"
            color={isInCompare ? 'primary' : 'default'}
            onClick={() => onCompare(userId)}
          >
            <CompareArrowsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <EmptyState missing="data" title="Could not load performance" description={error} />
      ) : !perf ? null : (
        <Grid container spacing={3}>
          {/* Spider chart */}
          <Grid item xs={12} md={6}>
            <InfoCard title="Skill Proficiency Radar">
              <SpiderChart primary={{ categoryAverages: perf.categoryAverages }} />
              <Typography className={classes.chartSubtitle}>
                Average proficiency (0–10) per skill category
              </Typography>
            </InfoCard>
          </Grid>

          {/* Linked repositories */}
          <Grid item xs={12} md={6}>
            <InfoCard title={`Repositories (${perf.linkedRepos.length})`}>
              {perf.linkedRepos.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No repositories linked
                </Typography>
              ) : (
                <Box style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {perf.linkedRepos.map(repo => (
                    <Box key={repo} className={classes.repoItem}>
                      <StorageIcon fontSize="small" color="action" />
                      <Typography className={classes.repoName}>
                        {repoNames.get(repo) ?? repo}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </InfoCard>
          </Grid>

          {/* Unique skills by category */}
          <Grid item xs={12}>
            <InfoCard title={`Skills (${perf.uniqueSkills.length} unique)`}>
              {groupedSkills.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No skills detected
                </Typography>
              ) : (
                <Box>
                  {groupedSkills.map(({ cat, skills }, idx) => (
                    <Box key={cat} className={classes.categorySection}>
                      <Typography className={classes.categoryTitle}>{cat}</Typography>
                      <Box display="flex" flexWrap="wrap">
                        {skills.map(skill => (
                          <Chip
                            key={skill}
                            label={skill}
                            size="small"
                            className={classes.skillChip}
                            style={{
                              backgroundColor: `${CATEGORY_COLORS[idx % CATEGORY_COLORS.length]}20`,
                              color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
                            }}
                          />
                        ))}
                      </Box>
                      {idx < groupedSkills.length - 1 && (
                        <Divider style={{ marginTop: 12 }} />
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </InfoCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
